import React, { useState, useEffect, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import api from '../services/api';
import { toast } from '../components/Toast';
import { confirm } from '../components/ConfirmDialog';
import { Package, Car, Home, Zap, Smartphone, Megaphone, Users, Sandwich, Brush, Pill, GraduationCap, Monitor, ClipboardList, TrendingDown, Download, Calendar, BarChart3, TrendingUp, Trophy, X, Search, Tag, List, FileText, Edit, Trash2, Store, Paperclip, RotateCcw, Hourglass, Check, Plus, ArrowUpDown, ArrowUp, ArrowDown, ChevronDown, LayoutGrid, Heart, Egg, HandHeart, HardHat } from 'lucide-react';

const BusinessExpenses = ({ projectMode = false }) => {
  const outletContext = useOutletContext();
  const projectId = projectMode ? outletContext?.projectId : null;
  const canManage = projectMode ? outletContext?.canManage : (outletContext?.canManageExpenses ?? true);
  const apiBase = projectMode && projectId ? `/projects/${projectId}/workspace` : '/business';

  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [sortField, setSortField] = useState('date_depense');
  const [sortOrder, setSortOrder] = useState('desc');
  const [dateRange, setDateRange] = useState({ from: '', to: '' });
  const [activePeriod, setActivePeriod] = useState('all');
  const [viewMode, setViewMode] = useState('table');
  const itemsPerPage = 15;

  const categoryIconMap = {
    'Fournitures': Package,
    'Transport': Car,
    'Loyer': Home,
    'Électricité': Zap,
    'Téléphone/Internet': Smartphone,
    'Marketing': Megaphone,
    'Salaires': Users,
    'Nourriture': Sandwich,
    'Entretien': Brush,
    'Santé': Pill,
    'Formation': GraduationCap,
    'Équipement': Monitor,
    'Zakat': Heart,
    'Pâques': Egg,
    'Social': HandHeart,
    'Construction': HardHat,
    'Autres': ClipboardList
  };

  const categories = [
    { nom: 'Fournitures' },
    { nom: 'Transport' },
    { nom: 'Loyer' },
    { nom: 'Électricité' },
    { nom: 'Téléphone/Internet' },
    { nom: 'Marketing' },
    { nom: 'Salaires' },
    { nom: 'Nourriture' },
    { nom: 'Entretien' },
    { nom: 'Santé' },
    { nom: 'Formation' },
    { nom: 'Équipement' },
    { nom: 'Zakat' },
    { nom: 'Pâques' },
    { nom: 'Social' },
    { nom: 'Construction' },
    { nom: 'Autres' }
  ];

  useEffect(() => {
    loadExpenses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, projectId]);

  const loadExpenses = async () => {
    try {
      let url = `${apiBase}/expenses`;
      if (filter !== 'all') url += `?category=${filter}`;
      const res = await api.get(url);
      setExpenses(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!await confirm('Supprimer cette dépense ?', { type: 'danger' })) return;
    try {
      await api.delete(`${apiBase}/expenses/${id}`);
      loadExpenses();
    } catch (err) {
      console.error(err);
    }
  };

  const fmt = (amount) => parseFloat(amount || 0).toLocaleString('fr-FR');

  // Stats
  const now = new Date();
  const thisMonth = expenses.filter(e => {
    const d = new Date(e.date_depense);
    return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  });
  const lastMonth = expenses.filter(e => {
    const d = new Date(e.date_depense);
    const lm = now.getMonth() === 0 ? 11 : now.getMonth() - 1;
    const ly = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();
    return d.getMonth() === lm && d.getFullYear() === ly;
  });

  const totalMonth = thisMonth.reduce((sum, e) => sum + parseFloat(e.montant || 0), 0);
  const totalLastMonth = lastMonth.reduce((sum, e) => sum + parseFloat(e.montant || 0), 0);
  const monthChange = totalLastMonth > 0 ? ((totalMonth - totalLastMonth) / totalLastMonth * 100).toFixed(0) : null;
  const totalAll = expenses.reduce((sum, e) => sum + parseFloat(e.montant || 0), 0);
  const avgPerExpense = expenses.length > 0 ? totalAll / expenses.length : 0;

  const byCategory = useMemo(() =>
    categories.map(cat => ({
      ...cat,
      total: expenses.filter(e => e.categorie === cat.nom).reduce((sum, e) => sum + parseFloat(e.montant || 0), 0),
      count: expenses.filter(e => e.categorie === cat.nom).length
    })).sort((a, b) => b.total - a.total),
    [expenses, categories]
  );

  // Filtrage, tri, pagination
  const filteredExpenses = useMemo(() => {
    let result = [...expenses];
    if (search) {
      const s = search.toLowerCase();
      result = result.filter(e =>
        e.description?.toLowerCase().includes(s) ||
        e.categorie?.toLowerCase().includes(s) ||
        e.fournisseur?.toLowerCase().includes(s) ||
        e.reference?.toLowerCase().includes(s)
      );
    }
    if (dateRange.from) result = result.filter(e => e.date_depense >= dateRange.from);
    if (dateRange.to) result = result.filter(e => e.date_depense <= dateRange.to);

    result.sort((a, b) => {
      let valA, valB;
      if (sortField === 'montant') {
        valA = parseFloat(a.montant || 0);
        valB = parseFloat(b.montant || 0);
      } else if (sortField === 'date_depense') {
        valA = a.date_depense || '';
        valB = b.date_depense || '';
      } else {
        valA = (a[sortField] || '').toLowerCase();
        valB = (b[sortField] || '').toLowerCase();
      }
      if (valA < valB) return sortOrder === 'asc' ? -1 : 1;
      if (valA > valB) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
    return result;
  }, [expenses, search, dateRange, sortField, sortOrder]);

  const totalPages = Math.ceil(filteredExpenses.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedExpenses = filteredExpenses.slice(startIndex, startIndex + itemsPerPage);
  const filteredTotal = filteredExpenses.reduce((sum, e) => sum + parseFloat(e.montant || 0), 0);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(o => o === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <span className="text-gray-300 ml-1"><ArrowUpDown size={14} className="inline" /></span>;
    return <span className="text-indigo-500 ml-1">{sortOrder === 'asc' ? <ArrowUp size={14} className="inline" /> : <ArrowDown size={14} className="inline" />}</span>;
  };

  // Export CSV
  const exportCSV = () => {
    const headers = ['Date', 'Catégorie', 'Description', 'Fournisseur', 'Référence', 'Montant', 'Notes'];
    const rows = filteredExpenses.map(e => [
      e.date_depense, e.categorie, e.description, e.fournisseur || '', e.reference || '', e.montant, (e.notes || '').replace(/\n/g, ' ')
    ]);
    const csvContent = [headers.join(';'), ...rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(';'))].join('\n');
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `depenses_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const getCatIcon = (catName) => { const Icon = categoryIconMap[catName] || ClipboardList; return <Icon size={16} />; };

  if (loading) {
    return (
      <div className="p-8 flex flex-col items-center justify-center gap-3">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-red-500"></div>
        <p className="text-gray-500 text-sm">Chargement des dépenses...</p>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-extrabold text-gray-900 flex items-center gap-2"><TrendingDown size={20} /> Dépenses</h1>
          <p className="text-gray-500 text-sm mt-1">Suivez et gérez vos dépenses professionnelles</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={exportCSV} className="px-3 py-2 bg-white border border-gray-200 text-gray-600 rounded-xl hover:bg-gray-50 transition text-sm flex items-center gap-1.5 shadow-sm" title="Exporter en CSV">
            <Download size={16} /> <span className="hidden sm:inline">Exporter</span>
          </button>
          {canManage && (
            <button onClick={() => { setEditingExpense(null); setShowModal(true); }} className="px-4 py-2 bg-gradient-to-r from-red-500 to-rose-600 text-white rounded-xl hover:from-red-600 hover:to-rose-700 transition flex items-center gap-2 text-sm font-semibold shadow-md hover:shadow-lg">
              <span>+</span> Nouvelle dépense
            </button>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-6">
        <div className="bg-gradient-to-br from-red-500 to-rose-600 rounded-2xl p-4 text-white shadow-lg">
          <div className="flex items-center justify-between mb-2">
            <span className="text-2xl"><Calendar size={24} /></span>
            {monthChange !== null && (
              <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${Number(monthChange) > 0 ? 'bg-red-300/30 text-red-100' : 'bg-green-300/30 text-green-100'}`}>
                {Number(monthChange) > 0 ? <ArrowUp size={14} className="inline" /> : <ArrowDown size={14} className="inline" />} {Math.abs(monthChange)}%
              </span>
            )}
          </div>
          <div className="text-2xl font-extrabold">{fmt(totalMonth)}</div>
          <div className="text-red-100 text-xs mt-1">Ce mois - {thisMonth.length} dépense{thisMonth.length > 1 ? 's' : ''}</div>
        </div>
        <div className="bg-white rounded-2xl p-4 shadow-md border border-gray-100">
          <div className="text-2xl mb-2"><BarChart3 size={24} /></div>
          <div className="text-2xl font-extrabold text-gray-900">{fmt(totalAll)}</div>
          <div className="text-gray-400 text-xs mt-1">Total - {expenses.length} dépense{expenses.length > 1 ? 's' : ''}</div>
        </div>
        <div className="bg-white rounded-2xl p-4 shadow-md border border-gray-100">
          <div className="text-2xl mb-2"><TrendingUp size={24} /></div>
          <div className="text-2xl font-extrabold text-gray-900">{fmt(avgPerExpense)}</div>
          <div className="text-gray-400 text-xs mt-1">Moyenne / dépense</div>
        </div>
        <div className="bg-white rounded-2xl p-4 shadow-md border border-gray-100">
          <div className="text-2xl mb-2">{(() => { const Icon = categoryIconMap[byCategory[0]?.nom] || Trophy; return <Icon size={24} />; })()}</div>
          <div className="text-lg font-extrabold text-gray-900 truncate">{byCategory[0]?.nom || 'N/A'}</div>
          <div className="text-gray-400 text-xs mt-1">Top catégorie - {fmt(byCategory[0]?.total || 0)} XOF</div>
        </div>
      </div>

      {/* Répartition par catégorie */}
      <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-5 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-gray-900">Répartition par catégorie</h3>
          {filter !== 'all' && (
            <button onClick={() => setFilter('all')} className="text-xs text-red-500 hover:text-red-700 font-medium flex items-center gap-1"><X size={14} /> Réinitialiser</button>
          )}
        </div>
        <div className="space-y-2">
          {byCategory.filter(c => c.total > 0).map((cat, i) => {
            const pct = totalAll > 0 ? (cat.total / totalAll * 100) : 0;
            const isActive = filter === cat.nom;
            return (
              <button key={i} onClick={() => setFilter(isActive ? 'all' : cat.nom)} className={`w-full flex items-center gap-3 p-2.5 rounded-xl text-left transition group ${isActive ? 'bg-red-50 ring-2 ring-red-400' : 'hover:bg-gray-50'}`}>
                <span className="text-xl w-8 text-center flex-shrink-0 flex items-center justify-center">{getCatIcon(cat.nom)}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-gray-800 truncate">{cat.nom}</span>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className="text-xs text-gray-400">{cat.count}</span>
                      <span className="text-sm font-bold text-gray-900">{fmt(cat.total)} <span className="text-xs font-normal text-gray-400">XOF</span></span>
                    </div>
                  </div>
                  <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full transition-all duration-500 ${isActive ? 'bg-red-500' : 'bg-red-400'}`} style={{ width: `${Math.max(2, pct)}%` }} />
                  </div>
                </div>
                <span className="text-xs text-gray-400 w-10 text-right flex-shrink-0">{pct.toFixed(0)}%</span>
              </button>
            );
          })}
          {byCategory.filter(c => c.total > 0).length === 0 && (
            <p className="text-gray-400 text-sm text-center py-4">Aucune dépense enregistrée</p>
          )}
        </div>
      </div>

      {/* Barre de recherche + filtres */}
      <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <input type="text" placeholder="Rechercher par description, catégorie, fournisseur..." value={search} onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }} className="w-full pl-10 pr-10 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-400 focus:border-red-400 text-sm" />
            <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm"><Search size={14} /></span>
            {search && <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-sm"><X size={14} /></button>}
          </div>
          <div className="flex gap-2">
            <div className="relative">
              <select
                value={filter}
                onChange={(e) => { setFilter(e.target.value); setCurrentPage(1); }}
                className={`appearance-none pl-8 pr-8 py-2.5 border rounded-xl text-sm cursor-pointer focus:ring-2 focus:ring-red-400 focus:border-red-400 transition ${
                  filter !== 'all' ? 'border-red-400 bg-red-50 text-red-700 font-medium' : 'border-gray-200 bg-white text-gray-600'
                }`}
              >
                <option value="all">Toutes catégories</option>
                {categories.map(cat => (
                <option key={cat.nom} value={cat.nom}>{cat.nom}</option>
                ))}
              </select>
              <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-sm pointer-events-none"><Tag size={14} /></span>
              <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-gray-400 pointer-events-none"><ChevronDown size={12} /></span>
            </div>
            <div className="flex bg-gray-100 rounded-xl p-0.5">
              <button onClick={() => setViewMode('table')} className={`px-3 py-1.5 rounded-lg text-sm transition ${viewMode === 'table' ? 'bg-white shadow-sm text-gray-800 font-medium' : 'text-gray-500'}`}><List size={16} /></button>
              <button onClick={() => setViewMode('cards')} className={`px-3 py-1.5 rounded-lg text-sm transition ${viewMode === 'cards' ? 'bg-white shadow-sm text-gray-800 font-medium' : 'text-gray-500'}`}><LayoutGrid size={16} /></button>
            </div>
          </div>
        </div>

        {/* Periode globale - toujours visible */}
        <div className="mt-3 pt-3 border-t border-gray-100">
          <div className="flex flex-wrap items-center gap-2 mb-2.5">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-1"><Calendar size={14} /> Période</span>
            <div className="flex flex-wrap gap-1.5">
              {[
                { key: 'all', label: 'Tout', fn: () => setDateRange({ from: '', to: '' }) },
                { key: 'today', label: "Aujourd'hui", fn: () => { const t = now.toISOString().split('T')[0]; setDateRange({ from: t, to: t }); }},
                { key: 'week', label: 'Cette semaine', fn: () => { const d = new Date(now); d.setDate(d.getDate() - d.getDay() + 1); setDateRange({ from: d.toISOString().split('T')[0], to: now.toISOString().split('T')[0] }); }},
                { key: 'month', label: 'Ce mois', fn: () => { const y = now.getFullYear(), m = String(now.getMonth() + 1).padStart(2, '0'); setDateRange({ from: `${y}-${m}-01`, to: `${y}-${m}-${new Date(y, now.getMonth() + 1, 0).getDate()}` }); }},
                { key: 'lastmonth', label: 'Mois dernier', fn: () => { const lm = new Date(now.getFullYear(), now.getMonth() - 1, 1); const end = new Date(now.getFullYear(), now.getMonth(), 0); setDateRange({ from: lm.toISOString().split('T')[0], to: end.toISOString().split('T')[0] }); }},
                { key: '3months', label: '3 derniers mois', fn: () => { const d = new Date(); d.setMonth(d.getMonth() - 3); setDateRange({ from: d.toISOString().split('T')[0], to: now.toISOString().split('T')[0] }); }},
                { key: '6months', label: '6 derniers mois', fn: () => { const d = new Date(); d.setMonth(d.getMonth() - 6); setDateRange({ from: d.toISOString().split('T')[0], to: now.toISOString().split('T')[0] }); }},
                { key: 'year', label: 'Cette année', fn: () => { setDateRange({ from: `${now.getFullYear()}-01-01`, to: now.toISOString().split('T')[0] }); }},
              ].map(s => (
                <button key={s.key} onClick={() => { setActivePeriod(s.key); s.fn(); setCurrentPage(1); }} className={`text-xs px-2.5 py-1.5 rounded-lg font-medium transition ${
                  activePeriod === s.key ? 'bg-red-500 text-white shadow-sm' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}>{s.label}</button>
              ))}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-gray-400">Personnalisé :</span>
            <div className="flex items-center gap-2">
              <label className="text-xs text-gray-500">Du</label>
              <input type="date" value={dateRange.from} onChange={(e) => { setDateRange(d => ({ ...d, from: e.target.value })); setActivePeriod('custom'); setCurrentPage(1); }} className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-red-400" />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-xs text-gray-500">Au</label>
              <input type="date" value={dateRange.to} onChange={(e) => { setDateRange(d => ({ ...d, to: e.target.value })); setActivePeriod('custom'); setCurrentPage(1); }} className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-red-400" />
            </div>
            {(dateRange.from || dateRange.to) && (
              <button onClick={() => { setDateRange({ from: '', to: '' }); setActivePeriod('all'); setCurrentPage(1); }} className="text-xs text-red-500 hover:text-red-700 font-medium px-2 py-1 hover:bg-red-50 rounded-lg transition flex items-center gap-1"><X size={14} /> Effacer</button>
            )}
            {(dateRange.from || dateRange.to) && (
              <span className="text-xs text-gray-400 ml-auto">
                {dateRange.from && dateRange.to ? `${new Date(dateRange.from).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })} - ${new Date(dateRange.to).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })}` :
                 dateRange.from ? `À partir du ${new Date(dateRange.from).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })}` :
                 `Jusqu'au ${new Date(dateRange.to).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })}`}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Info résultats + filtres actifs */}
      {(search || dateRange.from || dateRange.to || filter !== 'all') && (
        <div className="flex flex-wrap items-center justify-between gap-2 mb-4 px-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-sm text-gray-500">
              <span className="font-semibold text-gray-700">{filteredExpenses.length}</span> résultat{filteredExpenses.length > 1 ? 's' : ''}
              {search && <> pour "<span className="text-red-500">{search}</span>"</>}
              {' '}- Total : <span className="font-semibold text-red-600">{fmt(filteredTotal)} XOF</span>
            </p>
            {filter !== 'all' && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-100 text-red-700 rounded-full text-xs font-medium">
                <Tag size={14} /> {getCatIcon(filter)} {filter}
                <button onClick={() => { setFilter('all'); setCurrentPage(1); }} className="ml-0.5 text-red-500 hover:text-red-800 font-bold"><X size={14} /></button>
              </span>
            )}
          </div>
          {(search || filter !== 'all' || dateRange.from || dateRange.to) && (
            <button onClick={() => { setSearch(''); setFilter('all'); setDateRange({ from: '', to: '' }); setActivePeriod('all'); setCurrentPage(1); }} className="text-xs text-gray-500 hover:text-red-600 font-medium px-2 py-1 hover:bg-red-50 rounded-lg transition flex items-center gap-1">
              <RotateCcw size={14} /> Réinitialiser tout
            </button>
          )}
        </div>
      )}

      {/* Liste des dépenses */}
      {paginatedExpenses.length > 0 ? (
        viewMode === 'table' ? (
          <div className="bg-white rounded-2xl shadow-md border border-gray-100 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50/80 border-b border-gray-100">
                    {[
                      { key: 'date_depense', label: 'Date' },
                      { key: 'categorie', label: 'Catégorie' },
                      { key: 'description', label: 'Description' },
                      { key: 'fournisseur', label: 'Fournisseur', hide: true },
                      { key: 'montant', label: 'Montant', align: 'right' },
                    ].map(col => (
                      <th key={col.key} onClick={() => handleSort(col.key)} className={`px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700 select-none ${col.align === 'right' ? 'text-right' : 'text-left'} ${col.hide ? 'hidden md:table-cell' : ''}`}>
                        {col.label}<SortIcon field={col.key} />
                      </th>
                    ))}
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider w-24">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {paginatedExpenses.map(expense => (
                    <tr key={expense.id} className="hover:bg-red-50/30 transition-colors group">
                      <td className="px-4 py-3.5">
                        <div className="text-sm font-medium text-gray-900">{new Date(expense.date_depense).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })}</div>
                        <div className="text-xs text-gray-400">{new Date(expense.date_depense).getFullYear()}</div>
                      </td>
                      <td className="px-4 py-3.5">
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-gray-100 rounded-lg text-sm font-medium text-gray-700">{getCatIcon(expense.categorie)} {expense.categorie}</span>
                      </td>
                      <td className="px-4 py-3.5">
                        <div className="text-sm font-medium text-gray-900">{expense.description || '-'}</div>
                        {expense.reference && <div className="text-xs text-gray-400 mt-0.5">Réf: {expense.reference}</div>}
                        {expense.notes && <div className="text-xs text-gray-400 mt-0.5 truncate max-w-[200px]" title={expense.notes}><FileText size={14} className="inline" /> {expense.notes}</div>}
                      </td>
                      <td className="px-4 py-3.5 hidden md:table-cell">
                        <span className="text-sm text-gray-600">{expense.fournisseur || '-'}</span>
                      </td>
                      <td className="px-4 py-3.5 text-right">
                        <span className="text-sm font-bold text-red-600">-{fmt(expense.montant)} <span className="text-xs font-normal">XOF</span></span>
                      </td>
                      <td className="px-4 py-3.5">
                        {canManage ? (
                          <div className="flex justify-center gap-1">
                            <button onClick={() => { setEditingExpense(expense); setShowModal(true); }} className="p-1.5 text-amber-500 hover:bg-amber-50 rounded-lg transition" title="Modifier"><Edit size={16} /></button>
                            <button onClick={() => handleDelete(expense.id)} className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition" title="Supprimer"><Trash2 size={16} /></button>
                          </div>
                        ) : (
                          <span className="text-gray-300 text-xs">-</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && <Pagination currentPage={currentPage} totalPages={totalPages} startIndex={startIndex} itemsPerPage={itemsPerPage} total={filteredExpenses.length} setCurrentPage={setCurrentPage} />}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {paginatedExpenses.map(expense => (
                <div key={expense.id} className="bg-white rounded-2xl shadow-md border border-gray-100 p-4 hover:shadow-lg transition group">
                  <div className="flex items-start justify-between mb-3">
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-gray-100 rounded-lg text-xs font-medium text-gray-700">{getCatIcon(expense.categorie)} {expense.categorie}</span>
                    <span className="text-xs text-gray-400">{new Date(expense.date_depense).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                  </div>
                  <p className="text-sm font-semibold text-gray-900 mb-1">{expense.description || 'Sans description'}</p>
                  {expense.fournisseur && <p className="text-xs text-gray-400 mb-1"><Store size={14} className="inline" /> {expense.fournisseur}</p>}
                  {expense.reference && <p className="text-xs text-gray-400 mb-1"><Paperclip size={14} className="inline" /> Réf: {expense.reference}</p>}
                  {expense.notes && <p className="text-xs text-gray-400 truncate mb-2" title={expense.notes}><FileText size={14} className="inline" /> {expense.notes}</p>}
                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
                    <span className="text-lg font-extrabold text-red-600">-{fmt(expense.montant)} <span className="text-xs font-normal">XOF</span></span>
                    {canManage && (
                      <div className="flex gap-1">
                        <button onClick={() => { setEditingExpense(expense); setShowModal(true); }} className="p-1.5 text-amber-500 hover:bg-amber-50 rounded-lg" title="Modifier"><Edit size={16} /></button>
                        <button onClick={() => handleDelete(expense.id)} className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg" title="Supprimer"><Trash2 size={16} /></button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
            {totalPages > 1 && (
              <div className="flex justify-center mt-6 gap-1">
                <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="px-3 py-1.5 rounded-lg bg-white border border-gray-200 text-gray-500 text-sm hover:bg-gray-50 disabled:opacity-30">«</button>
                <span className="px-4 py-1.5 text-sm text-gray-600">{currentPage} / {totalPages}</span>
                <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="px-3 py-1.5 rounded-lg bg-white border border-gray-200 text-gray-500 text-sm hover:bg-gray-50 disabled:opacity-30">»</button>
              </div>
            )}
          </>
        )
      ) : (
        <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-12 text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-red-50 rounded-full mb-4"><TrendingDown size={40} /></div>
          <h3 className="text-lg font-bold text-gray-900 mb-2">{search || dateRange.from || dateRange.to ? 'Aucun résultat' : 'Aucune dépense'}</h3>
          <p className="text-gray-500 text-sm mb-6">{search || dateRange.from || dateRange.to ? "Essayez d'ajuster vos filtres" : 'Commencez par enregistrer votre première dépense'}</p>
          {!search && !dateRange.from && !dateRange.to && canManage && (
            <button onClick={() => { setEditingExpense(null); setShowModal(true); }} className="px-6 py-2.5 bg-gradient-to-r from-red-500 to-rose-600 text-white rounded-xl font-semibold hover:from-red-600 hover:to-rose-700 transition shadow-md">+ Nouvelle dépense</button>
          )}
        </div>
      )}

      {showModal && (
        <ExpenseModal expense={editingExpense} categories={categories} onClose={() => setShowModal(false)} onSuccess={() => { setShowModal(false); loadExpenses(); }} apiBase={apiBase} />
      )}
    </div>
  );
};

// Pagination Component
const Pagination = ({ currentPage, totalPages, startIndex, itemsPerPage, total, setCurrentPage }) => (
  <div className="px-4 py-3 bg-gray-50/50 border-t border-gray-100 flex flex-col sm:flex-row items-center justify-between gap-2">
    <div className="text-xs text-gray-400">{startIndex + 1}-{Math.min(startIndex + itemsPerPage, total)} sur {total}</div>
    <div className="flex gap-1">
      <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1} className="px-2.5 py-1 rounded-lg bg-white border border-gray-200 text-gray-500 text-xs hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed">««</button>
      <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="px-2.5 py-1 rounded-lg bg-white border border-gray-200 text-gray-500 text-xs hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed">«</button>
      {(() => {
        const pages = [];
        const show = new Set([1, totalPages]);
        for (let i = Math.max(1, currentPage - 1); i <= Math.min(totalPages, currentPage + 1); i++) show.add(i);
        const sorted = [...show].sort((a, b) => a - b);
        sorted.forEach((page, idx) => {
          if (idx > 0 && page - sorted[idx - 1] > 1) pages.push(<span key={`e-${page}`} className="px-1.5 text-gray-300 text-xs">...</span>);
          pages.push(<button key={page} onClick={() => setCurrentPage(page)} className={`px-3 py-1 rounded-lg text-xs font-medium transition ${currentPage === page ? 'bg-red-500 text-white shadow-sm' : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'}`}>{page}</button>);
        });
        return pages;
      })()}
      <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="px-2.5 py-1 rounded-lg bg-white border border-gray-200 text-gray-500 text-xs hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed">»</button>
      <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} className="px-2.5 py-1 rounded-lg bg-white border border-gray-200 text-gray-500 text-xs hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed">»»</button>
    </div>
  </div>
);

// Expense Modal Component
const ExpenseModal = ({ expense, categories, onClose, onSuccess, apiBase = '/business' }) => {
  const [form, setForm] = useState({
    categorie: expense?.categorie || categories[0]?.nom || '',
    description: expense?.description || '',
    montant: expense?.montant || '',
    fournisseur: expense?.fournisseur || '',
    reference: expense?.reference || '',
    date_depense: expense?.date_depense?.split(' ')[0] || new Date().toISOString().split('T')[0]
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (parseFloat(form.montant) <= 0) { toast.warning('Le montant doit être supérieur à 0'); return; }
    setSaving(true);
    try {
      if (expense) {
        await api.put(`${apiBase}/expenses/${expense.id}`, form);
      } else {
        await api.post(`${apiBase}/expenses`, form);
      }
      onSuccess();
    } catch (err) {
      console.error(err);
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden max-h-[90vh] flex flex-col">
        <div className="bg-gradient-to-r from-red-500 to-rose-600 p-5 text-white flex items-center justify-between flex-shrink-0">
          <div>
            <h3 className="text-lg font-bold">{expense ? <><Edit size={16} className="inline" /> Modifier la dépense</> : <><Plus size={16} className="inline" /> Nouvelle dépense</>}</h3>
            <p className="text-red-100 text-xs mt-0.5">Remplissez les informations ci-dessous</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 transition text-white"><X size={14} /></button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4 overflow-y-auto flex-1">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">Catégorie *</label>
              <select value={form.categorie} onChange={(e) => setForm({ ...form, categorie: e.target.value })} className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-red-400 focus:border-red-400" required>
                {categories.map((cat, index) => (
                  <option key={index} value={cat.nom}>{cat.nom}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">Date *</label>
              <input type="date" value={form.date_depense} onChange={(e) => setForm({ ...form, date_depense: e.target.value })} className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-red-400 focus:border-red-400" required />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">Description *</label>
            <input type="text" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-red-400 focus:border-red-400" placeholder="Ex: Achat de fournitures de bureau" required />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">Montant (XOF) *</label>
            <div className="relative">
              <input type="number" min="1" step="1" value={form.montant} onChange={(e) => setForm({ ...form, montant: e.target.value })} className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-red-400 focus:border-red-400 text-lg font-bold pr-14" placeholder="0" required />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm font-medium">XOF</span>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">Fournisseur</label>
              <input type="text" value={form.fournisseur} onChange={(e) => setForm({ ...form, fournisseur: e.target.value })} className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-red-400 focus:border-red-400" placeholder="Nom du fournisseur" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">Référence</label>
              <input type="text" value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-red-400 focus:border-red-400" placeholder="N° facture" />
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 py-2.5 bg-gray-100 text-gray-600 rounded-xl font-medium hover:bg-gray-200 transition text-sm">Annuler</button>
            <button type="submit" disabled={saving} className="flex-1 py-2.5 bg-gradient-to-r from-red-500 to-rose-600 text-white rounded-xl font-bold hover:from-red-600 hover:to-rose-700 disabled:opacity-50 transition text-sm shadow-md">
              {saving ? <><Hourglass size={14} className="inline" /> Enregistrement...</> : expense ? <><Check size={16} /> Modifier</> : <><Plus size={16} /> Ajouter</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BusinessExpenses;
